import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, ExternalLink } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
      toast.success("Demande envoyée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2">
      <div className="bg-neutral-950 text-white p-10 md:p-16 flex-col justify-between hidden md:flex">
        <div className="overline text-white/60">— MML · Mot de passe oublié</div>
        <div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-none mb-6">
            Réinitialisez<br />en toute<br />sécurité.
          </h1>
          <p className="text-white/60 max-w-sm">
            Saisissez votre email. Si un compte existe, un lien sécurisé valable 1 heure sera créé.
          </p>
        </div>
        <div className="font-mono text-xs text-white/40">SHA-256 · TTL 1h · usage unique</div>
      </div>
      <div className="p-10 md:p-16 flex items-center justify-center">
        <div className="w-full max-w-md">
          <Link to="/login" className="text-sm text-neutral-600 hover:text-black inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" /> Retour connexion
          </Link>
          <h2 className="font-display font-black text-3xl tracking-tighter mb-2">Mot de passe oublié ?</h2>
          <p className="text-sm text-neutral-600 mb-8">
            Entrez l'email associé à votre compte. Vous recevrez un lien de réinitialisation.
          </p>

          {!result && (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div>
                <Label className="overline" htmlFor="email">Email</Label>
                <Input id="email" type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="rounded-md mt-2 h-11" data-testid="forgot-email" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full rounded-full bg-[#1F4032] hover:bg-[#2A5443] h-12 gap-2"
                data-testid="forgot-submit">
                <Mail className="w-4 h-4" /> {loading ? "Envoi…" : "Envoyer le lien"}
              </Button>
            </form>
          )}

          {result && (
            <div className="space-y-4" data-testid="forgot-result">
              <div className="p-5 rounded-md bg-emerald-50 border border-emerald-200">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-emerald-900">{result.message}</div>
                    {result.email_sent && (
                      <p className="text-sm text-emerald-900/80 mt-2">
                        Vérifiez votre boîte de réception (et vos spams).
                        Le lien est valable <b>1 heure</b>, à usage unique.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {result.dev_reset_link && (
                <div className="p-4 rounded-md bg-amber-50 border border-amber-200 text-sm">
                  <div className="font-semibold text-amber-900 mb-2">⚠ Email non envoyé — lien direct</div>
                  <p className="text-amber-900/80 text-xs mb-3">
                    {result.dev_note}
                    {result.dev_email_error && <><br/>Erreur : <span className="font-mono">{result.dev_email_error}</span></>}
                  </p>
                  <a href={result.dev_reset_link}
                    className="inline-flex items-center gap-2 text-xs font-mono break-all underline text-amber-900 hover:text-black"
                    data-testid="dev-reset-link">
                    <ExternalLink className="w-3 h-3 shrink-0" />
                    Ouvrir le lien de réinitialisation
                  </a>
                </div>
              )}
              <Button onClick={() => { setResult(null); setEmail(""); }}
                variant="outline" className="w-full rounded-full border-neutral-300">
                Faire une autre demande
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
